import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { observarSincronizacao, sincronizar, type EstadoSincronizacao } from '../dados/sincronizacao.js'

/// Faixa fina que diz se o trabalho do inspetor já saiu do aparelho.
///
/// É a informação mais importante da tela para quem trabalha offline: sem ela, a
/// pessoa não sabe se pode desinstalar o app, trocar de celular, ou se aquela
/// interdição das 7h realmente chegou ao SESMT.

export function BarraSincronizacao() {
  const [estado, setEstado] = useState<EstadoSincronizacao>({
    fase: 'parado',
    ultimaEm: null,
    pendentes: 0,
  })
  const [online, setOnline] = useState(navigator.onLine)
  // Na tela de preenchimento, a faixa de "sincronizando" e de "aguardando envio"
  // aparecia e sumia a cada ciclo, bem acima do formulário, e empurrava a tela
  // inteira — às vezes tirando o campo de baixo do dedo. Lá, a própria tela diz
  // "Salvo no aparelho". Sem conexão e erro continuam aparecendo: são estados que
  // duram e que a pessoa precisa ver.
  const preenchendo = /^\/inspecao\/[^/]+$/.test(useLocation().pathname)

  useEffect(() => observarSincronizacao(setEstado), [])

  useEffect(() => {
    const muda = () => setOnline(navigator.onLine)
    window.addEventListener('online', muda)
    window.addEventListener('offline', muda)
    return () => {
      window.removeEventListener('online', muda)
      window.removeEventListener('offline', muda)
    }
  }, [])

  // Durante a sincronização o contador está em movimento e não é publicado.
  const pendentes = estado.fase === 'sincronizando' ? null : estado.pendentes

  if (!online) {
    return (
      <Faixa cor="bg-slate-700">
        <Ponto className="bg-slate-300" />
        Sem conexão — seu trabalho está salvo no aparelho
        {pendentes !== null && pendentes > 0 && ` · ${pendentes} aguardando envio`}
      </Faixa>
    )
  }

  if (estado.fase === 'sincronizando') {
    if (preenchendo) return null
    return (
      <Faixa cor="bg-marinho-600">
        <Ponto className="animate-pulse bg-limao" />
        Sincronizando — {estado.passo}
      </Faixa>
    )
  }

  if (estado.fase === 'erro') {
    return (
      <Faixa cor="bg-moderada">
        <Ponto className="bg-white" />
        {estado.mensagem}
        {estado.pendentes > 0 && ` · ${estado.pendentes} na fila`}
        <button onClick={() => void sincronizar({ forcado: true })} className="ml-auto underline">
          Tentar agora
        </button>
      </Faixa>
    )
  }

  if (estado.pendentes > 0 && !preenchendo) {
    return (
      <Faixa cor="bg-marinho-600">
        <Ponto className="bg-limao" />
        {estado.pendentes} {estado.pendentes === 1 ? 'inspeção aguardando' : 'inspeções aguardando'} envio
        <button onClick={() => void sincronizar({ forcado: true })} className="ml-auto underline">
          Enviar agora
        </button>
      </Faixa>
    )
  }

  // Tudo enviado e nada pendente: não ocupa espaço na tela.
  return null
}

function Faixa({ cor, children }: { cor: string; children: React.ReactNode }) {
  return (
    <div className={`${cor} py-1.5 text-xs text-white`}>
      <div className="largura flex items-center gap-2">{children}</div>
    </div>
  )
}

function Ponto({ className }: { className: string }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${className}`} />
}
